import { atom } from 'recoil';

/** Thứ tự trên thanh tab kiểu xem: Overview, Bảng, Thời gian, Kết hợp, Lịch, Kanban. */
export const APV2_VIEW_TAB_ORDER = ['1', '2', '3', '4', '5', '6'] as const;
export type Apv2ViewTabKey = (typeof APV2_VIEW_TAB_ORDER)[number];
/** Tab tùy chọn (không gồm Bảng — luôn hiện). */
export type Apv2OptionalViewTabKey = Exclude<Apv2ViewTabKey, '2'>;

/** Tab luôn hiện trên header. */
export const APV2_PRIMARY_VIEW_TAB_KEYS: Apv2ViewTabKey[] = ['2'];

export type Apv2ViewTabDef = {
  key: Apv2ViewTabKey;
  labelKey: string;
  defaultLabel: string;
  /** Chưa mở dùng. */
  comingSoon?: boolean;
};

/** Toàn bộ tab view (VIEWS_MIGRATION_PLAN: Overview / Thời gian / Kết hợp / Lịch đã chuyển sang bản V2). */
export const APV2_ALL_VIEW_TABS: Apv2ViewTabDef[] = [
  { key: '2', labelKey: 'bsc.title.bang', defaultLabel: 'Bảng' },
  { key: '6', labelKey: 'bsc.title.kanbanview', defaultLabel: 'Kanban' },
  { key: '1', labelKey: 'bsc.label.overview', defaultLabel: 'Overview' },
  { key: '3', labelKey: 'bsc.title.ganttchart', defaultLabel: 'Thời gian' },
  { key: '4', labelKey: 'bsc.title.kethop', defaultLabel: 'Kết hợp' },
  { key: '5', labelKey: 'bsc.title.calendarview', defaultLabel: 'Lịch' },
];

export const APV2_ACTIVE_VIEW_TAB_KEY: Apv2ViewTabKey = '2';

/** Tab tùy chọn hiện mặc định: Kanban. */
export const APV2_DEFAULT_OPTIONAL_VISIBLE_TAB_KEYS: Apv2OptionalViewTabKey[] = ['6'];

/** Tab tùy chọn được phép hiện trên header. */
export const APV2_ALLOWED_OPTIONAL_VISIBLE_TAB_KEYS: readonly Apv2OptionalViewTabKey[] = ['6', '1', '3', '4', '5'];

export function isApv2ViewTabComingSoon(key: string): boolean {
  const def = APV2_ALL_VIEW_TABS.find((t) => t.key === key);
  return Boolean(def?.comingSoon);
}

/** @deprecated Dùng APV2_ALL_VIEW_TABS; giữ alias cho code cũ. */
export const APV2_OPTIONAL_VIEW_TABS: Apv2ViewTabDef[] = APV2_ALL_VIEW_TABS.filter(
  (t): t is Apv2ViewTabDef & { key: Apv2OptionalViewTabKey } => t.key !== '2'
);

export type Apv2WorkspaceViewPrefs = {
  optionalVisibleTabKeys: Apv2OptionalViewTabKey[];
  activeTabKey: Apv2ViewTabKey;
};

const STORAGE_KEY = 'apv2-workspace-view-prefs-v2';

const DEFAULT_PREFS: Apv2WorkspaceViewPrefs = {
  optionalVisibleTabKeys: [...APV2_DEFAULT_OPTIONAL_VISIBLE_TAB_KEYS],
  activeTabKey: APV2_ACTIVE_VIEW_TAB_KEY,
};

const OPTIONAL_KEY_SET = new Set<string>(APV2_OPTIONAL_VIEW_TABS.map((t) => t.key));
const ALLOWED_OPTIONAL_KEY_SET = new Set<string>(APV2_ALLOWED_OPTIONAL_VISIBLE_TAB_KEYS);

function sanitizeOptionalKeys(keys: unknown): Apv2OptionalViewTabKey[] {
  if (!Array.isArray(keys)) return [...APV2_DEFAULT_OPTIONAL_VISIBLE_TAB_KEYS];
  const next = keys.filter(
    (k): k is Apv2OptionalViewTabKey =>
      OPTIONAL_KEY_SET.has(String(k)) && ALLOWED_OPTIONAL_KEY_SET.has(String(k))
  );
  return next.length > 0 ? [...new Set(next)] : [...APV2_DEFAULT_OPTIONAL_VISIBLE_TAB_KEYS];
}

function loadFromStorage(): Apv2WorkspaceViewPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<Apv2WorkspaceViewPrefs>;
    return {
      optionalVisibleTabKeys: sanitizeOptionalKeys(parsed.optionalVisibleTabKeys),
      activeTabKey: APV2_ACTIVE_VIEW_TAB_KEY,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

/** Mọi kiểu xem đã mở dùng, theo thứ tự APV2_VIEW_TAB_ORDER (thanh tab trên header). */
export function getApv2VisibleViewTabKeys(): Apv2ViewTabKey[] {
  const allowed = new Set<string>(APV2_ALLOWED_OPTIONAL_VISIBLE_TAB_KEYS);
  return APV2_VIEW_TAB_ORDER.filter((key) => key === '2' || allowed.has(key));
}

export const apv2WorkspaceViewAtom = atom<Apv2WorkspaceViewPrefs>({
  key: 'apv2WorkspaceViewPrefs',
  default: DEFAULT_PREFS,
  effects: [
    ({ setSelf, onSet }) => {
      if (typeof window !== 'undefined') {
        setSelf(loadFromStorage());
      }
      let persistTimer: ReturnType<typeof setTimeout> | null = null;
      onSet((newValue) => {
        if (typeof window === 'undefined') return;
        if (persistTimer) clearTimeout(persistTimer);
        persistTimer = setTimeout(() => {
          persistTimer = null;
          try {
            const prefs: Apv2WorkspaceViewPrefs = {
              optionalVisibleTabKeys: sanitizeOptionalKeys(newValue.optionalVisibleTabKeys),
              activeTabKey: APV2_ACTIVE_VIEW_TAB_KEY,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
          } catch (error) {
            console.error('Error saving APV2 workspace view prefs:', error);
          }
        }, 250);
      });
    },
  ],
});

/** Nút "Hôm nay" trên header → Timeline cuộn về hôm nay (tăng số để phát lệnh). */
export const apv2TimelineTodayAtom = atom<number>({
  key: 'apv2TimelineToday',
  default: 0,
});

/** Thang thời gian của Timeline (Thời gian / Kết hợp). */
export type Apv2TimelineScale = 'week' | 'month' | 'quarter' | 'year';
const TIMELINE_SCALE_STORAGE_KEY = 'apv2-timeline-scale';
const TIMELINE_SCALES: readonly Apv2TimelineScale[] = ['week', 'month', 'quarter', 'year'];

export const apv2TimelineScaleAtom = atom<Apv2TimelineScale>({
  key: 'apv2TimelineScale',
  default: 'week',
  effects: [
    ({ setSelf, onSet }) => {
      if (typeof window === 'undefined') return;
      try {
        const saved = localStorage.getItem(TIMELINE_SCALE_STORAGE_KEY);
        if (saved && (TIMELINE_SCALES as readonly string[]).includes(saved)) setSelf(saved as Apv2TimelineScale);
      } catch {
        // Bị chặn localStorage → dùng mặc định.
      }
      onSet((next) => {
        try {
          localStorage.setItem(TIMELINE_SCALE_STORAGE_KEY, next);
        } catch {
          // Bỏ qua — chỉ là tiện ích nhớ lựa chọn.
        }
      });
    },
  ],
});

/**
 * Cụm «Thêm cột động / Quản lý cột» nằm ở thanh lọc từng sheet; dialog vẫn thuộc Header.
 * Sheet gửi lệnh (kèm mục tiêu của sheet để điền sẵn), Header mở dialog.
 */
export const apv2ColumnCommandAtom = atom<{ type: 'add' | 'manage'; mtid?: string; n: number }>({
  key: 'apv2ColumnCommand',
  default: { type: 'add', n: 0 },
});

/** Header ghi lý do khoá cụm nút cột (null = dùng được): chưa chọn bộ phận / còn thay đổi chưa lưu. */
export const apv2ColumnActionsBlockedAtom = atom<string | null>({
  key: 'apv2ColumnActionsBlocked',
  default: null,
});

/** true khi Header workspace (chủ của dialog cột) đang mount — trang khác dùng bảng (vd. Tổng quan) thì ẩn cụm nút cột. */
export const apv2ColumnToolsAvailableAtom = atom<boolean>({
  key: 'apv2ColumnToolsAvailable',
  default: false,
});

/** Atom boolean nhớ trong localStorage ('1' / '0'); chặn localStorage → dùng mặc định. */
function persistedBooleanAtom(key: string, storageKey: string, defaultValue: boolean) {
  return atom<boolean>({
    key,
    default: defaultValue,
    effects: [
      ({ setSelf, onSet }) => {
        if (typeof window === 'undefined') return;
        try {
          const saved = localStorage.getItem(storageKey);
          if (saved === '1' || saved === '0') setSelf(saved === '1');
        } catch {
          // Bỏ qua.
        }
        onSet((next) => {
          try {
            localStorage.setItem(storageKey, next ? '1' : '0');
          } catch {
            // Bỏ qua — chỉ là tiện ích nhớ lựa chọn.
          }
        });
      },
    ],
  });
}

/** Tooltip «Nhấn chuột phải…» khi rê dòng bảng — bật/tắt ở bóng đèn mẹo, chỉ trong phiên (không lưu). */
export const apv2RowContextHintEnabledAtom = atom<boolean>({
  key: 'apv2RowContextHintEnabled',
  default: true,
});

/** Đã mở bóng đèn mẹo thao tác ít nhất 1 lần (ẩn chấm nhấp nháy). */
export const apv2TipsSeenAtom = persistedBooleanAtom('apv2TipsSeen', 'apv2-tips-seen', false);
